import { ChevronDown, ChevronUp, ChevronsUpDown, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { alignClass } from './column-align.ts';
import type { DataTableColumn, DataTableSort } from './types.ts';

const ariaSortByDirection: Record<
  DataTableSort['direction'],
  'ascending' | 'descending'
> = {
  asc: 'ascending',
  desc: 'descending',
};

const sortIconByDirection: Record<DataTableSort['direction'], LucideIcon> = {
  asc: ChevronUp,
  desc: ChevronDown,
};

function nextSort(current: DataTableSort | undefined, key: string): DataTableSort {
  if (current?.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }

  return { key, direction: 'asc' };
}

interface TableHeadProps<T> {
  columns: DataTableColumn<T>[];
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  hasActions: boolean;
}

/** The sticky header row: a sort control on every sortable column. */
export function TableHead<T>({
  columns,
  sort,
  onSortChange,
  hasActions,
}: TableHeadProps<T>) {
  return (
    <thead>
      <tr>
        {columns.map((column) => (
          <HeaderCell
            key={column.key}
            column={column}
            sort={sort}
            onSortChange={onSortChange}
          />
        ))}
        {hasActions && (
          <th
            scope='col'
            className='sticky top-0 z-1 w-0 bg-surface px-3.5 py-2.5 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-line max-mobile:h-11'
          >
            <span className='sr-only'>Actions</span>
          </th>
        )}
      </tr>
    </thead>
  );
}

interface HeaderCellProps<T> {
  column: DataTableColumn<T>;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
}

function HeaderCell<T>({ column, sort, onSortChange }: HeaderCellProps<T>) {
  const active = sort?.key === column.key ? sort : undefined;
  const ariaSort = active ? ariaSortByDirection[active.direction] : 'none';
  const SortIcon = active ? sortIconByDirection[active.direction] : undefined;

  return (
    <th
      scope='col'
      aria-sort={column.sortable ? ariaSort : undefined}
      className={cn(
        'sticky top-0 z-1 bg-surface px-3.5 py-2.5 text-xs font-bold tracking-wide whitespace-nowrap text-ink-muted uppercase max-mobile:h-11',
        'after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-line',
        alignClass[column.align ?? 'start'],
        column.width,
        column.hideBelow === 'mobile' && 'max-mobile:hidden',
      )}
    >
      {column.sortable ? (
        <button
          type='button'
          onClick={() => onSortChange?.(nextSort(sort, column.key))}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1 uppercase transition-colors hover:text-ink',
            /* A finger needs the whole header cell, not the 16px of its label. */
            'max-mobile:before:absolute max-mobile:before:inset-0',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
            active && 'text-ink',
          )}
        >
          {column.header}
          {SortIcon ? (
            <SortIcon className='size-3' aria-hidden='true' />
          ) : (
            <ChevronsUpDown className='size-3 text-ink-faint' aria-hidden='true' />
          )}
        </button>
      ) : (
        column.header
      )}
    </th>
  );
}
